/**
 * Deterministic SVG renderers for the plot model.
 *
 * Renders a PlotModel to an SVG string (and a DOM helper). Output is a pure
 * function of the model, so it can be snapshot-tested without a browser. All
 * graphics use plain SVG - no charting library. Colors come from CSS custom
 * properties so graphs follow the light/dark theme.
 */

import type {
  PlotModel,
  NumberLineModel,
  CoordinateModel,
  BoxPlotModel,
  AngleDiagramModel,
} from "./plot-model";

const STROKE = "currentColor";
const ACCENT = "var(--accent, #1a4d2e)";

export function renderPlotSvg(model: PlotModel): string {
  switch (model.kind) {
    case "number-line":
      return renderNumberLine(model);
    case "coordinate":
      return renderCoordinate(model);
    case "box-plot":
      return renderBoxPlot(model);
    case "angle-diagram":
      return renderAngle(model);
  }
}

/** Create an SVG element node from a model (for insertion into the DOM). */
export function plotElement(model: PlotModel): SVGElement {
  const wrap = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  const tmp = document.createElement("div");
  tmp.innerHTML = renderPlotSvg(model);
  const svg = tmp.querySelector("svg");
  return (svg as SVGElement) ?? wrap;
}

// --- Number line ---------------------------------------------------------

const NL_W = 360;
const NL_H = 64;
const NL_PAD = 20;
const NL_AXIS_Y = 34;

function nlX(model: NumberLineModel, value: number): number {
  const span = model.max - model.min || 1;
  const usable = NL_W - 2 * NL_PAD;
  return NL_PAD + ((value - model.min) / span) * usable;
}

function renderNumberLine(model: NumberLineModel): string {
  const parts: string[] = [];
  parts.push(
    `<line x1="${NL_PAD}" y1="${NL_AXIS_Y}" x2="${NL_W - NL_PAD}" y2="${NL_AXIS_Y}" stroke="${STROKE}" stroke-width="2"/>`,
  );

  for (let t = Math.ceil(model.min); t <= Math.floor(model.max); t++) {
    const x = nlX(model, t);
    parts.push(
      `<line x1="${x.toFixed(1)}" y1="${NL_AXIS_Y - 5}" x2="${x.toFixed(1)}" y2="${NL_AXIS_Y + 5}" stroke="${STROKE}" stroke-width="1"/>`,
    );
    parts.push(
      `<text x="${x.toFixed(1)}" y="${NL_AXIS_Y + 20}" font-size="11" text-anchor="middle" fill="${STROKE}">${t}</text>`,
    );
  }

  for (const ray of model.rays) {
    const from = nlX(model, ray.from.toNumber());
    const to = ray.direction === "left" ? NL_PAD : NL_W - NL_PAD;
    parts.push(
      `<line x1="${from.toFixed(1)}" y1="${NL_AXIS_Y}" x2="${to.toFixed(1)}" y2="${NL_AXIS_Y}" stroke="${ACCENT}" stroke-width="4"/>`,
    );
    const dir = ray.direction === "left" ? -1 : 1;
    const tip = to;
    parts.push(
      `<path d="M ${tip.toFixed(1)} ${NL_AXIS_Y} l ${(-dir * 8).toFixed(1)} -5 l 0 10 z" fill="${ACCENT}"/>`,
    );
    parts.push(endpointCircle(from, NL_AXIS_Y, ray.closed));
  }

  for (const pt of model.points) {
    const x = nlX(model, pt.value.toNumber());
    parts.push(endpointCircle(x, NL_AXIS_Y, pt.closed));
  }

  return svgWrap(NL_W, NL_H, parts.join(""), "Number line");
}

function endpointCircle(cx: number, cy: number, closed: boolean): string {
  if (closed) {
    return `<circle cx="${cx.toFixed(1)}" cy="${cy}" r="6" fill="${ACCENT}"/>`;
  }
  return `<circle cx="${cx.toFixed(1)}" cy="${cy}" r="6" fill="var(--surface, #fff)" stroke="${ACCENT}" stroke-width="2.5"/>`;
}

// --- Coordinate plane ----------------------------------------------------

const CP = 280;
const CP_PAD = 24;

function cpX(model: CoordinateModel, x: number): number {
  const span = model.xMax - model.xMin || 1;
  return CP_PAD + ((x - model.xMin) / span) * (CP - 2 * CP_PAD);
}
function cpY(model: CoordinateModel, y: number): number {
  const span = model.yMax - model.yMin || 1;
  return CP - CP_PAD - ((y - model.yMin) / span) * (CP - 2 * CP_PAD);
}

function renderCoordinate(model: CoordinateModel): string {
  const parts: string[] = [];

  for (let gx = Math.ceil(model.xMin); gx <= Math.floor(model.xMax); gx++) {
    const x = cpX(model, gx);
    parts.push(
      `<line x1="${x.toFixed(1)}" y1="${CP_PAD}" x2="${x.toFixed(1)}" y2="${CP - CP_PAD}" stroke="var(--border, #ddd)" stroke-width="0.5"/>`,
    );
  }
  for (let gy = Math.ceil(model.yMin); gy <= Math.floor(model.yMax); gy++) {
    const y = cpY(model, gy);
    parts.push(
      `<line x1="${CP_PAD}" y1="${y.toFixed(1)}" x2="${CP - CP_PAD}" y2="${y.toFixed(1)}" stroke="var(--border, #ddd)" stroke-width="0.5"/>`,
    );
  }

  if (model.xMin <= 0 && model.xMax >= 0) {
    const x0 = cpX(model, 0);
    parts.push(
      `<line x1="${x0.toFixed(1)}" y1="${CP_PAD}" x2="${x0.toFixed(1)}" y2="${CP - CP_PAD}" stroke="${STROKE}" stroke-width="1.5"/>`,
    );
  }
  if (model.yMin <= 0 && model.yMax >= 0) {
    const y0 = cpY(model, 0);
    parts.push(
      `<line x1="${CP_PAD}" y1="${y0.toFixed(1)}" x2="${CP - CP_PAD}" y2="${y0.toFixed(1)}" stroke="${STROKE}" stroke-width="1.5"/>`,
    );
  }

  for (const seg of model.segments) {
    const x1 = cpX(model, seg.from.x.toNumber());
    const y1 = cpY(model, seg.from.y.toNumber());
    const x2 = cpX(model, seg.to.x.toNumber());
    const y2 = cpY(model, seg.to.y.toNumber());
    parts.push(
      `<line x1="${x1.toFixed(1)}" y1="${y1.toFixed(1)}" x2="${x2.toFixed(1)}" y2="${y2.toFixed(1)}" stroke="${ACCENT}" stroke-width="2.5"/>`,
    );
  }

  for (const pt of model.points) {
    const x = cpX(model, pt.x.toNumber());
    const y = cpY(model, pt.y.toNumber());
    const closed = pt.closed !== false;
    parts.push(
      closed
        ? `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="4.5" fill="${ACCENT}"/>`
        : `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="4.5" fill="var(--surface, #fff)" stroke="${ACCENT}" stroke-width="2"/>`,
    );
    if (pt.label) {
      parts.push(
        `<text x="${(x + 6).toFixed(1)}" y="${(y - 6).toFixed(1)}" font-size="11" fill="${STROKE}">${escapeText(pt.label)}</text>`,
      );
    }
  }

  return svgWrap(CP, CP, parts.join(""), "Coordinate graph");
}

// --- Box plot ------------------------------------------------------------

const BP_W = 360;
const BP_H = 96;
const BP_PAD = 24;
const BP_BOX_TOP = 26;
const BP_BOX_BOT = 58;
const BP_MID = (BP_BOX_TOP + BP_BOX_BOT) / 2;

function renderBoxPlot(model: BoxPlotModel): string {
  const axisMin = model.axisMin ?? niceFloor(model.min);
  const axisMax = model.axisMax ?? niceCeil(model.max);
  const span = axisMax - axisMin || 1;
  const usable = BP_W - 2 * BP_PAD;
  const px = (v: number): number => BP_PAD + ((v - axisMin) / span) * usable;

  const parts: string[] = [];

  // Axis with integer-ish ticks.
  parts.push(
    `<line x1="${BP_PAD}" y1="${BP_H - 16}" x2="${BP_W - BP_PAD}" y2="${BP_H - 16}" stroke="${STROKE}" stroke-width="1.5"/>`,
  );
  const step = tickStep(span);
  for (let t = Math.ceil(axisMin / step) * step; t <= axisMax; t += step) {
    const x = px(t);
    parts.push(
      `<line x1="${x.toFixed(1)}" y1="${BP_H - 20}" x2="${x.toFixed(1)}" y2="${BP_H - 12}" stroke="${STROKE}" stroke-width="1"/>`,
    );
    parts.push(
      `<text x="${x.toFixed(1)}" y="${BP_H - 2}" font-size="10" text-anchor="middle" fill="${STROKE}">${round1(t)}</text>`,
    );
  }

  const xMin = px(model.min);
  const xQ1 = px(model.q1);
  const xMed = px(model.median);
  const xQ3 = px(model.q3);
  const xMax = px(model.max);

  // Whiskers.
  parts.push(
    `<line x1="${xMin.toFixed(1)}" y1="${BP_MID}" x2="${xQ1.toFixed(1)}" y2="${BP_MID}" stroke="${STROKE}" stroke-width="1.5"/>`,
  );
  parts.push(
    `<line x1="${xQ3.toFixed(1)}" y1="${BP_MID}" x2="${xMax.toFixed(1)}" y2="${BP_MID}" stroke="${STROKE}" stroke-width="1.5"/>`,
  );
  // Whisker caps.
  for (const cx of [xMin, xMax]) {
    parts.push(
      `<line x1="${cx.toFixed(1)}" y1="${BP_BOX_TOP + 3}" x2="${cx.toFixed(1)}" y2="${BP_BOX_BOT - 3}" stroke="${STROKE}" stroke-width="1.5"/>`,
    );
  }
  // Box.
  parts.push(
    `<rect x="${xQ1.toFixed(1)}" y="${BP_BOX_TOP}" width="${(xQ3 - xQ1).toFixed(1)}" height="${BP_BOX_BOT - BP_BOX_TOP}" fill="${ACCENT}" fill-opacity="0.18" stroke="${ACCENT}" stroke-width="2"/>`,
  );
  // Median line.
  parts.push(
    `<line x1="${xMed.toFixed(1)}" y1="${BP_BOX_TOP}" x2="${xMed.toFixed(1)}" y2="${BP_BOX_BOT}" stroke="${ACCENT}" stroke-width="2.5"/>`,
  );

  return svgWrap(BP_W, BP_H, parts.join(""), "Box plot");
}

// --- Angle diagram -------------------------------------------------------

const AG = 220;
const AG_CX = 40;
const AG_CY = AG - 40;
const AG_LEN = 150;

function renderAngle(model: AngleDiagramModel): string {
  const parts: string[] = [];
  for (const deg of model.rays) {
    const rad = (deg * Math.PI) / 180;
    const x = AG_CX + AG_LEN * Math.cos(rad);
    const y = AG_CY - AG_LEN * Math.sin(rad);
    parts.push(
      `<line x1="${AG_CX}" y1="${AG_CY}" x2="${x.toFixed(1)}" y2="${y.toFixed(1)}" stroke="${ACCENT}" stroke-width="3"/>`,
    );
  }
  // Small arc between the first two rays.
  if (model.rays.length >= 2) {
    const a0 = (model.rays[0] as number) * (Math.PI / 180);
    const a1 = (model.rays[1] as number) * (Math.PI / 180);
    const r = 34;
    const sx = AG_CX + r * Math.cos(a0);
    const sy = AG_CY - r * Math.sin(a0);
    const ex = AG_CX + r * Math.cos(a1);
    const ey = AG_CY - r * Math.sin(a1);
    parts.push(
      `<path d="M ${sx.toFixed(1)} ${sy.toFixed(1)} A ${r} ${r} 0 0 0 ${ex.toFixed(1)} ${ey.toFixed(1)}" fill="none" stroke="${STROKE}" stroke-width="1.5"/>`,
    );
    if (model.label) {
      const mid = (a0 + a1) / 2;
      const lx = AG_CX + (r + 16) * Math.cos(mid);
      const ly = AG_CY - (r + 16) * Math.sin(mid);
      parts.push(
        `<text x="${lx.toFixed(1)}" y="${ly.toFixed(1)}" font-size="13" text-anchor="middle" fill="${STROKE}">${escapeText(model.label)}</text>`,
      );
    }
  }
  parts.push(`<circle cx="${AG_CX}" cy="${AG_CY}" r="3" fill="${STROKE}"/>`);
  return svgWrap(AG, AG, parts.join(""), "Angle diagram");
}

// --- Shared --------------------------------------------------------------

function svgWrap(w: number, h: number, inner: string, label: string): string {
  return (
    `<svg viewBox="0 0 ${w} ${h}" width="100%" style="max-width:${w}px;height:auto" ` +
    `role="img" aria-label="${label}" xmlns="http://www.w3.org/2000/svg">${inner}</svg>`
  );
}

function escapeText(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function niceFloor(v: number): number {
  return Math.floor(v - Math.max(1, Math.abs(v) * 0.1));
}
function niceCeil(v: number): number {
  return Math.ceil(v + Math.max(1, Math.abs(v) * 0.1));
}
function tickStep(span: number): number {
  const raw = span / 6;
  const pow = Math.pow(10, Math.floor(Math.log10(raw || 1)));
  const candidates = [1, 2, 5, 10].map((c) => c * pow);
  return candidates.find((c) => c >= raw) ?? (10 * pow);
}
function round1(v: number): string {
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}
